import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell,
} from 'recharts';
import {
  Users, Wallet, Banknote, TrendingDown, Clock3, Landmark, Hourglass,
  MailCheck, RefreshCw, ListChecks,
} from 'lucide-react';

import usePayrollEntity from '../../../utils/usePayrollEntity';
import { fmtRs } from '../../../utils/payrollFormat';
import { StatGrid, ChartCard } from '../../../components/dashboard';
import SearchableSelect from '../../../components/common/SearchableSelect';
import {
  fetchPayrollHeadersByPeriod,
  fetchPayrollEntityList,
  selectPayrollHeaders,
} from '../../../store/slices/payrollSlice';
import { fetchAllEmployees } from '../../../store/slices/employeeSlice';

const FIELD =
  'w-full h-9 bg-white border border-gray-200 rounded-lg text-xs px-2.5 outline-none focus:border-primary-500 text-gray-700';
const LABEL = 'text-[11px] font-medium text-gray-500 mb-1 block';
const CARD = 'bg-white border border-gray-100 rounded-xl p-5 shadow-sm';
const tooltipStyle = { borderRadius: 8, fontSize: 12, border: '1px solid #e5e7eb' };

/* Shown on widgets the backend has no endpoint for yet. */
const NO_API_TEXT = 'Not available yet';

/* Compact money for KPI tiles — "Rs. 2.85M" instead of "Rs. 2,845,300.00". */
const fmtCompactRs = (n) => {
  const v = Number(n) || 0;
  const abs = Math.abs(v);
  if (abs >= 1_000_000) return `Rs. ${(v / 1_000_000).toFixed(2)}M`;
  if (abs >= 1_000) return `Rs. ${(v / 1_000).toFixed(1)}K`;
  return fmtRs(v);
};

const num = (v) => Number(v) || 0;

const DONUT_COLORS = ['#059669', '#dc2626', '#2563eb', '#d97706', '#7c3aed'];

/* ── Page ──────────────────────────────────────────────────────────────────
   Every number here comes from the backend:
     • Payroll periods, loans, batches, overtime, salaries → payroll entity APIs
     • Employees / Gross / Net / Deductions / Top earners → processed payroll
       headers for the selected period (Payroll Header page uses the same API)
     • Department names for overtime → employee master
   Widgets with no backend endpoint yet (bank transfer, payslip e-mail status,
   recent activity) show an empty "Not available yet" state — no sample data. */
const PayrollDashboard = () => {
  const dispatch = useDispatch();

  const { list: periods } = usePayrollEntity('payrollPeriods');
  const { list: loans } = usePayrollEntity('loanManagement');
  const { list: batches } = usePayrollEntity('batchManagement');
  const { list: overtime } = usePayrollEntity('overtimeEntry');
  const { list: salaries } = usePayrollEntity('employeeSalary');

  const { data: headers = [], loading: headersLoading } =
    useSelector(selectPayrollHeaders) || { data: [], loading: false };
  const { employees = [] } = useSelector((s) => s.employees || { employees: [] });

  useEffect(() => {
    if (!employees.length) dispatch(fetchAllEmployees());
  }, [dispatch, employees.length]);

  /* Latest period by year/month — chosen once the periods arrive. */
  const sortedPeriods = useMemo(
    () => [...periods].sort((a, b) => (num(b.year) - num(a.year)) || (num(b.month) - num(a.month))),
    [periods]
  );
  const [selectedPeriodId, setSelectedPeriodId] = useState('');

  useEffect(() => {
    if (!selectedPeriodId && sortedPeriods.length) {
      setSelectedPeriodId(String(sortedPeriods[0].id));
    }
  }, [sortedPeriods, selectedPeriodId]);

  /* Processed payroll for the selected period. */
  useEffect(() => {
    if (selectedPeriodId) dispatch(fetchPayrollHeadersByPeriod(selectedPeriodId));
  }, [dispatch, selectedPeriodId]);

  const handlePeriodChange = (id) => setSelectedPeriodId(id);

  const refresh = () => {
    ['payrollPeriods', 'loanManagement', 'batchManagement', 'overtimeEntry', 'employeeSalary']
      .forEach((key) => dispatch(fetchPayrollEntityList(key)));
    if (selectedPeriodId) dispatch(fetchPayrollHeadersByPeriod(selectedPeriodId));
  };

  const inPeriod = (id) => String(id) === String(selectedPeriodId);

  /* Employee id → name / department, for headers and overtime rows. */
  const nameById = useMemo(() => {
    const m = {};
    salaries.forEach((s) => { if (s.employeeId != null) m[String(s.employeeId)] = s.name; });
    employees.forEach((e) => {
      const id = String(e.HED_EMPLOYEE_ID ?? '');
      if (id && !m[id]) m[id] = e.HED_REPORT_NAME || `${e.HED_FIRST_NAME || ''} ${e.HED_LAST_NAME || ''}`.trim();
    });
    return m;
  }, [salaries, employees]);

  const deptById = useMemo(() => {
    const m = {};
    employees.forEach((e) => {
      const id = String(e.HED_EMPLOYEE_ID ?? '');
      if (id) m[id] = e.HDD_DEPARTMENT_NAME || 'Unassigned';
    });
    return m;
  }, [employees]);

  /* ── This period's processed payroll ─────────────────────────────────── */
  const payrollRows = useMemo(
    () => (Array.isArray(headers) ? headers : []).map((h) => ({
      id: h.HPH_PAYROLL_ID,
      employeeId: h.HPH_EMPLOYEE_ID,
      name: nameById[String(h.HPH_EMPLOYEE_ID)] || `Employee #${h.HPH_EMPLOYEE_ID}`,
      gross: num(h.HPH_GROSS_EARNINGS),
      deductions: num(h.HPH_TOTAL_DEDUCTIONS),
      net: num(h.HPH_NET_SALARY),
    })),
    [headers, nameById]
  );

  const periodOvertime = useMemo(
    () => overtime.filter((r) => inPeriod(r.payrollPeriodId)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [overtime, selectedPeriodId]
  );
  const periodBatches = useMemo(
    () => batches.filter((b) => inPeriod(b.periodId)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [batches, selectedPeriodId]
  );

  /* ── Totals ──────────────────────────────────────────────────────────── */
  const totalGross = useMemo(() => payrollRows.reduce((s, r) => s + r.gross, 0), [payrollRows]);
  const totalNet = useMemo(() => payrollRows.reduce((s, r) => s + r.net, 0), [payrollRows]);
  const totalDeductions = useMemo(() => payrollRows.reduce((s, r) => s + r.deductions, 0), [payrollRows]);
  const totalOvertime = useMemo(() => periodOvertime.reduce((s, r) => s + num(r.amount), 0), [periodOvertime]);
  const totalLoanBalance = useMemo(() => loans.reduce((s, r) => s + num(r.balance), 0), [loans]);
  const pendingCount = periodBatches.filter((b) => b.status !== 'Authorized').length;

  /* ── Payroll breakdown donut ─────────────────────────────────────────── */
  const breakdownDonut = useMemo(
    () => [
      { name: 'Net Pay', value: totalNet },
      { name: 'Deductions', value: totalDeductions },
    ],
    [totalNet, totalDeductions]
  );

  /* ── Overtime by department ──────────────────────────────────────────── */
  const otByDept = useMemo(() => {
    const buckets = {};
    periodOvertime.forEach((r) => {
      const dept = deptById[String(r.employeeId)] || 'Unassigned';
      buckets[dept] = (buckets[dept] || 0) + num(r.amount);
    });
    return Object.entries(buckets)
      .map(([department, amount]) => ({ department, amount }))
      .sort((a, b) => b.amount - a.amount);
  }, [periodOvertime, deptById]);

  /* ── No backend endpoint yet — kept empty, never filled with sample data ── */
  const bankStatusDonut = [];
  const payslipDonut = [];
  const recentActivity = [];

  /* ── Batch status for the period ─────────────────────────────────────── */
  const approvalStatus = useMemo(() => [
    { status: 'Pending', count: pendingCount },
    { status: 'Approved', count: periodBatches.length - pendingCount },
  ], [periodBatches, pendingCount]);

  /* ── Top earners ──────────────────────────────────────────────────────── */
  const topEarners = useMemo(
    () => [...payrollRows].sort((a, b) => b.net - a.net).slice(0, 5),
    [payrollRows]
  );
  const maxNet = Math.max(1, ...topEarners.map((r) => r.net || 0));

  const noPreview = payrollRows.length === 0;
  const noOvertime = otByDept.length === 0;
  const noBank = true;
  const noPayslips = true;
  const noApprovals = periodBatches.length === 0;
  const noEarners = topEarners.length === 0;

  const stats = [
    { label: 'Employees', value: headersLoading ? '…' : payrollRows.length, sub: 'In this payroll run', icon: Users, tone: 'primary' },
    { label: 'Gross Payroll', value: fmtCompactRs(totalGross), sub: 'Before deductions', icon: Wallet, tone: 'blue' },
    { label: 'Net Payroll', value: fmtCompactRs(totalNet), sub: 'After deductions', icon: Banknote, tone: 'emerald' },
    { label: 'Deductions', value: fmtCompactRs(totalDeductions), sub: 'EPF, PAYE & other', icon: TrendingDown, tone: 'red' },
    { label: 'Overtime Cost', value: fmtCompactRs(totalOvertime), sub: 'This period', icon: Clock3, tone: 'amber' },
    { label: 'Loan Balance', value: fmtCompactRs(totalLoanBalance), sub: 'Outstanding across staff', icon: Landmark, tone: 'violet' },
    { label: 'Pending Batches', value: pendingCount, sub: 'Awaiting approval', icon: Hourglass, tone: 'orange' },
    { label: 'Payslips Sent', value: '—', sub: NO_API_TEXT, icon: MailCheck, tone: 'indigo' },
  ];

  return (
    <div className="p-6 space-y-4">
      {/* ── Filters — same shell/field classes as OTDashboard's filter bar ── */}
      <div className="bg-white border border-gray-100 rounded-xl px-4 py-3.5 shadow-sm">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div>
            <span className={LABEL}>Payroll Period</span>
            <SearchableSelect
              value={selectedPeriodId}
              onChange={handlePeriodChange}
              options={sortedPeriods.map((period) => ({
                value: String(period.id),
                label: `${period.code} - ${period.name}`,
              }))}
              placeholder="Select payroll period"
              searchPlaceholder="Search period..."
              className={FIELD}
            />
          </div>
          <div className="flex items-end gap-2 md:col-span-3">
            <button
              type="button"
              onClick={refresh}
              className="flex items-center gap-2 h-9 px-4 bg-primary-600 text-white rounded-lg text-xs font-semibold hover:bg-primary-700 transition-colors"
            >
              <RefreshCw size={14} />
              Refresh
            </button>
          </div>
        </div>
      </div>

      {/* ── KPI cards ────────────────────────────────────────────────────── */}
      <StatGrid stats={stats} columns={8} />

      {/* ── Row 1: OT by dept (span 2) + payroll breakdown + bank status ──── */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        <div className="lg:col-span-2">
          <ChartCard title="Overtime Cost by Department" subtitle={`Total: ${fmtRs(totalOvertime)}`} empty={noOvertime} emptyText="No overtime for this period">
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={otByDept} margin={{ top: 8, right: 8, left: -12, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                <XAxis dataKey="department" tick={{ fontSize: 10, fill: '#94a3b8' }} />
                <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} />
                <Tooltip contentStyle={tooltipStyle} formatter={(v) => [fmtRs(v), 'OT Cost']} />
                <Bar dataKey="amount" fill="#d97706" radius={[4, 4, 0, 0]} maxBarSize={40} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        </div>

        <ChartCard title="Payroll Breakdown" subtitle={`Gross: ${fmtRs(totalGross)}`} empty={noPreview} emptyText={headersLoading ? 'Loading…' : 'Payroll not processed for this period'}>
          <ResponsiveContainer width="100%" height={190}>
            <PieChart>
              <Pie data={breakdownDonut} dataKey="value" nameKey="name" innerRadius={48} outerRadius={74} paddingAngle={2}>
                {breakdownDonut.map((_, i) => (
                  <Cell key={i} fill={DONUT_COLORS[i % DONUT_COLORS.length]} />
                ))}
              </Pie>
              <Tooltip contentStyle={tooltipStyle} formatter={(v, n) => [fmtRs(v), n]} />
            </PieChart>
          </ResponsiveContainer>
          <div className="flex flex-wrap justify-center gap-x-3 gap-y-1.5 mt-2 text-[11px] text-gray-600">
            {breakdownDonut.map((d, i) => (
              <span key={d.name} className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full" style={{ backgroundColor: DONUT_COLORS[i % DONUT_COLORS.length] }} />
                {d.name} <span className="font-semibold text-gray-800">{fmtRs(d.value)}</span>
              </span>
            ))}
          </div>
        </ChartCard>

        <ChartCard title="Bank Transfer Status" subtitle="Bank file status" empty={noBank} emptyText={NO_API_TEXT}>
          <ResponsiveContainer width="100%" height={190}>
            <PieChart>
              <Pie data={bankStatusDonut} dataKey="value" nameKey="name" innerRadius={48} outerRadius={74} paddingAngle={2}>
                {bankStatusDonut.map((_, i) => (
                  <Cell key={i} fill={DONUT_COLORS[i % DONUT_COLORS.length]} />
                ))}
              </Pie>
              <Tooltip contentStyle={tooltipStyle} formatter={(v, n) => [v, n]} />
            </PieChart>
          </ResponsiveContainer>
          <div className="flex flex-wrap justify-center gap-x-3 gap-y-1.5 mt-2 text-[11px] text-gray-600">
            {bankStatusDonut.map((d, i) => (
              <span key={d.name} className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full" style={{ backgroundColor: DONUT_COLORS[i % DONUT_COLORS.length] }} />
                {d.name} <span className="font-semibold text-gray-800">{d.value}</span>
              </span>
            ))}
          </div>
        </ChartCard>
      </div>

      {/* ── Row 2: top earners + batch approval + payslip delivery ─────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <ChartCard title="Top Earners" subtitle="Highest net pay this run" empty={noEarners} emptyText={headersLoading ? 'Loading…' : 'Payroll not processed for this period'}>
          <div className="space-y-3.5">
            {topEarners.map((r, i) => (
              <div key={r.id ?? r.empNo} className="flex items-center gap-3">
                <span
                  className={`w-6 h-6 shrink-0 rounded-full text-white text-[11px] font-bold flex items-center justify-center ${
                    i < 3 ? 'bg-primary-500' : 'bg-gray-300'
                  }`}
                >
                  {i + 1}
                </span>
                <span
                  className={`w-20 shrink-0 text-xs truncate ${i < 3 ? 'text-primary-600 font-medium' : 'text-gray-600'}`}
                  title={r.name}
                >
                  {r.name}
                </span>
                <div className="flex-1 h-1.5 bg-gray-100 rounded-full relative">
                  <div
                    className="absolute inset-y-0 left-0 bg-primary-500 rounded-full"
                    style={{ width: `${((r.net || 0) / maxNet) * 100}%` }}
                  />
                </div>
                <span className="w-20 text-right text-xs font-semibold text-gray-700 tabular-nums">
                  {fmtRs(r.net)}
                </span>
              </div>
            ))}
          </div>
        </ChartCard>

        <ChartCard title="Batch Approval Status" subtitle="Payroll batches in this period" empty={noApprovals} emptyText="No batches for this period">
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={approvalStatus} margin={{ top: 8, right: 8, left: -12, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
              <XAxis dataKey="status" tick={{ fontSize: 11, fill: '#94a3b8' }} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: '#94a3b8' }} />
              <Tooltip contentStyle={tooltipStyle} formatter={(v) => [v, 'Batches']} />
              <Bar dataKey="count" radius={[4, 4, 0, 0]} maxBarSize={40}>
                {approvalStatus.map((entry, i) => (
                  <Cell
                    key={i}
                    fill={entry.status === 'Approved' ? '#059669' : entry.status === 'Rejected' ? '#dc2626' : '#d97706'}
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Payslip Delivery" subtitle="Emailed to employees" empty={noPayslips} emptyText={NO_API_TEXT}>
          <ResponsiveContainer width="100%" height={190}>
            <PieChart>
              <Pie data={payslipDonut} dataKey="value" nameKey="name" innerRadius={48} outerRadius={74} paddingAngle={2}>
                {payslipDonut.map((_, i) => (
                  <Cell key={i} fill={DONUT_COLORS[i % DONUT_COLORS.length]} />
                ))}
              </Pie>
              <Tooltip contentStyle={tooltipStyle} formatter={(v, n) => [v, n]} />
            </PieChart>
          </ResponsiveContainer>
          <div className="flex flex-wrap justify-center gap-x-3 gap-y-1.5 mt-2 text-[11px] text-gray-600">
            {payslipDonut.map((d, i) => (
              <span key={d.name} className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full" style={{ backgroundColor: DONUT_COLORS[i % DONUT_COLORS.length] }} />
                {d.name} <span className="font-semibold text-gray-800">{d.value}</span>
              </span>
            ))}
          </div>
        </ChartCard>
      </div>

      {/* ── Recent activity ──────────────────────────────────────────────── */}
      <div className={CARD}>
        <div className="flex items-center gap-2 mb-4">
          <ListChecks size={16} className="text-primary-700" />
          <h3 className="text-sm font-semibold text-gray-800">Recent Activity</h3>
        </div>
        {recentActivity.length === 0 ? (
          <div className="h-[80px] flex items-center justify-center text-gray-400 text-sm">{NO_API_TEXT}</div>
        ) : (
          <div className="divide-y divide-gray-100">
            {recentActivity.map((log) => (
              <div key={log.id} className="flex items-center justify-between gap-3 py-2.5 first:pt-0 last:pb-0">
                <div className="min-w-0">
                  <div className="text-[13px] font-medium text-gray-900 truncate">{log.action}</div>
                  <div className="text-[11px] text-gray-400">
                    {log.module} · {log.performedBy}
                  </div>
                </div>
                <span className="text-[11px] text-gray-400 tabular-nums shrink-0">{log.timestamp}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default PayrollDashboard;